export type ProcessStep = {
  label: string;
  detail: string;
  when?: string;
};

/**
 * Numbered vertical timeline used on the high-intent "what order does this
 * happen in" guides. Pure markup — no images, so it stays sharp and fast.
 */
export function ProcessTimeline({
  title,
  steps,
  note,
}: {
  title: string;
  steps: ReadonlyArray<ProcessStep>;
  note?: string | undefined;
}) {
  return (
    <figure className="mb-12 rounded-sm border border-border bg-secondary/40 p-6 sm:p-8">
      <figcaption className="font-serif text-xl leading-snug text-foreground">{title}</figcaption>
      <ol className="mt-7 space-y-0">
        {steps.map((step, i) => (
          <li key={step.label} className="relative flex gap-4 pb-7 last:pb-0">
            {i < steps.length - 1 && (
              <span
                className="absolute left-[15px] top-9 bottom-0 w-px bg-border"
                aria-hidden="true"
              />
            )}
            <span className="relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border border-accent bg-card text-xs font-semibold text-accent-foreground">
              {i + 1}
            </span>
            <div className="min-w-0 pt-1">
              <p className="text-sm font-semibold text-foreground">
                {step.label}
                {step.when && (
                  <span className="ml-2 text-xs font-normal uppercase tracking-wider text-accent-foreground/70">
                    {step.when}
                  </span>
                )}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      {note && (
        <p className="mt-6 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
          {note}
        </p>
      )}
    </figure>
  );
}
